import { useState, useMemo, useEffect } from "react";
import { Loader2 } from "lucide-react";
import { useSharedVoices, useVoiceCategories, useRefreshVoices, useVoices } from "@/hooks/voice/useVoices";
import { usePreselectedVoiceList } from "@/hooks/voice/usePreselectedVoices";
import { VoiceViewTab } from "@/types/voice/favorites";
import { Voice } from "@/types/voice/voice";
import { VoiceFilterState, VoiceTtsTarget, VoiceViewMode } from "@/types/voice/voice-playground";
import { useVoiceFavorites, useToggleFavorite } from '@/hooks/voice/useVoiceFavorites';
import { VoiceFilters } from "@/components/voice/VoiceFilters";
import { toast } from "sonner";

import {
  VoiceCreateModal,
  VoiceGrid,
  VoicePlaygroundHeader,
  VoiceEmptyState,
  VoiceTtsSheet,
} from "./components";

import { useVoiceFiltering } from "./hooks/useVoiceFiltering";
import { useVoicePlayback } from "./hooks/useVoicePlayback";
import { useTabFiltering } from "./hooks/useTabFiltering";

const VoicePlaygroundPage = () => {
  // Tab state management - consolidates all tab-related state
  const {
    activeTab,
    currentTab,
    switchTab,
    updateSearch,
    updateFilter,
    resetFilters,
    loadMore,
  } = useTabFiltering('recommended');

  // UI state
  const [viewMode, setViewMode] = useState<VoiceViewMode>("list");
  const [isTabTransitioning, setIsTabTransitioning] = useState(false);
  const [ttsSheetOpen, setTtsSheetOpen] = useState(false);
  const [ttsVoice, setTtsVoice] = useState<VoiceTtsTarget | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);

  // Accumulated voices for the server-side paginated "all" tab
  const [accumulatedAllVoices, setAccumulatedAllVoices] = useState<Voice[]>([]);
  const [prevAllVoicesFilters, setPrevAllVoicesFilters] = useState<VoiceFilterState | undefined>(undefined);

  // The curated preselected voices, joined against the live catalog for preview_url/labels
  const { data: preselectedRows = [], isLoading: preselectedLoading } = usePreselectedVoiceList();
  const { data: voicesResult, isLoading: voicesLoading } = useVoices();

  const { data: allVoicesData, isLoading: allVoicesLoading } = useSharedVoices({
    enabled: activeTab === 'all',
    page: currentTab.page,
    pageSize: 100,
    search: currentTab.search || undefined,
    category: currentTab.filters.category,
    accent: currentTab.filters.accent,
    language: currentTab.filters.language,
    age: currentTab.filters.age,
    gender: currentTab.filters.gender,
  });

  // Accumulate voices from paginated results for 'all' tab
  useEffect(() => {
    if (activeTab === "all" && allVoicesData?.voices) {
      const filtersChanged = JSON.stringify(prevAllVoicesFilters) !== JSON.stringify(currentTab.filters);
      if (filtersChanged || currentTab.page === 0) {
        setAccumulatedAllVoices(allVoicesData.voices);
        setPrevAllVoicesFilters(currentTab.filters);
      } else {
        setAccumulatedAllVoices(prev => {
          const voiceIds = new Set(prev.map(v => v.id));
          const newVoices = allVoicesData.voices.filter(v => !voiceIds.has(v.id));
          return [...prev, ...newVoices];
        });
      }
    } else if (activeTab !== "all") {
      setAccumulatedAllVoices([]);
      setPrevAllVoicesFilters(undefined);
    }
  }, [allVoicesData, currentTab.page, activeTab, currentTab.filters]);

  const allVoices = accumulatedAllVoices;
  const allVoicesHasMore = allVoicesData?.hasMore || false;
  const allVoicesTotalCount = allVoicesHasMore ? Math.max(allVoices.length, allVoicesData?.totalCount || 0) : (allVoicesData?.totalCount || 0);

  const recommendedLoading = preselectedLoading || voicesLoading;
  const isLoadingAllVoices = allVoicesLoading;
  const isInitialLoading = recommendedLoading && activeTab === "recommended";

  const categories = useVoiceCategories(activeTab === 'all');
  const { mutate: refreshVoices, isPending: isRefreshing } = useRefreshVoices();

  // Favorites
  const { data: favorites = [], isError: favoritesError } = useVoiceFavorites();
  const { mutate: toggleFavorite } = useToggleFavorite();
  if (favoritesError) console.log("Favorites table not ready yet");

  const favoriteVoiceIds = useMemo(
    () => new Set(favorites.map(f => f.voice_id)),
    [favorites]
  );

  // Preselected voices joined against the live catalog for preview_url/name/labels
  const recommendedVoicesList = useMemo(() => {
    const liveVoices = voicesResult?.voices ?? [];
    return preselectedRows
      .filter((row) => row.is_active)
      .map((row) => liveVoices.find((v) => v.id === row.voice_id))
      .filter((v): v is Voice => !!v && !!v.id);
  }, [preselectedRows, voicesResult]);

  const favoriteVoices = useMemo(() => {
    return favorites
      .map(f => f.voice_data)
      .filter((v): v is Voice => !!v && !!v.id);
  }, [favorites]);

  // Client-side filtering for non-paginated tabs (recommended is now a fixed small list)
  const {
    filters,
    filterOptions,
    filteredVoices,
    visibleFilteredVoices,
    visibleVoiceCount,
    updateFilter: updateClientFilter,
    resetFilters: resetClientFilters,
    loadMoreVoices
  } = useVoiceFiltering({
    allVoices,
    recommendedVoices: recommendedVoicesList,
    favoriteVoices,
    activeTab,
    allRecommendedVoices: recommendedVoicesList
  });

  const { playingVoiceId, isGeneratingPreview, playVoicePreview } = useVoicePlayback();

  const handleRefresh = () => {
    refreshVoices();
  };

  const handleToggleFavorite = (voice: any) => {
    if (!favoritesError) {
      const isFavorited = favoriteVoiceIds.has(voice.id);
      toggleFavorite({
        voiceId: voice.id,
        voice,
        isFavorited,
        isSharedVoice: activeTab === 'all'
      });

      if (isFavorited) {
        toast.success(`Removed ${voice.name} from favorites`);
      } else {
        toast.success(`Added ${voice.name} to favorites`);
      }
    } else {
      toast.error("Favorites feature is not available yet. Please run the database migration.");
    }
  };

  const handleTryTextToSpeech = (voice: any) => {
    setTtsVoice({ id: voice.id, name: voice.name });
    setTtsSheetOpen(true);
  };

  const handleTabChange = (tab: VoiceViewTab) => {
    setIsTabTransitioning(true);
    switchTab(tab);
    setTimeout(() => setIsTabTransitioning(false), 150);
  };

  const handleFilterChange = (next: any) => {
    if (activeTab === "all") {
      // Server-side filtering
      if (next.search !== undefined) updateSearch(next.search);
      if (next.category !== undefined) updateFilter('category', next.category);
      if (next.accent !== undefined) updateFilter('accent', next.accent);
      if (next.age !== undefined) updateFilter('age', next.age);
      if (next.gender !== undefined) updateFilter('gender', next.gender);
      if (next.language !== undefined) updateFilter('language', next.language);
    } else {
      // Client-side filtering for recommended, favorites
      if (next.search !== undefined) updateClientFilter('searchQuery', next.search);
      if (next.category !== undefined) updateClientFilter('category', next.category);
      if (next.accent !== undefined) updateClientFilter('accent', next.accent);
      if (next.age !== undefined) updateClientFilter('age', next.age);
      if (next.gender !== undefined) updateClientFilter('gender', next.gender);
      if (next.language !== undefined) updateClientFilter('language', next.language);
    }
    if (next.viewMode !== undefined) setViewMode(next.viewMode);
    if (next.tab !== undefined) handleTabChange(next.tab);
  };

  const handleReset = () => {
    if (activeTab === "all") {
      resetFilters();
    } else {
      resetClientFilters();
    }
  };

  if (isInitialLoading) {
    return (
      <div className="min-h-screen bg-[#F9FAFC] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-genie-primary" />
          <p className="text-genie-text-sub">Loading voices...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-[#F9FAFC]">
        <div className="container mx-auto px-4 py-8 max-w-[1920px]">
          <div className="w-full">
            <VoicePlaygroundHeader
              onRefresh={handleRefresh}
              isRefreshing={isRefreshing}
              onCreateVoice={() => setCreateModalOpen(true)}
            />

            <div className="bg-white rounded-lg mb-8 shadow-sm border border-gray-200">
              <VoiceFilters
                value={{
                  search: activeTab === "all" ? currentTab.searchInput : filters.searchQuery,
                  category: activeTab === "all" ? currentTab.filters.category : filters.category,
                  accent: activeTab === "all" ? currentTab.filters.accent : filters.accent,
                  age: activeTab === "all" ? currentTab.filters.age : filters.age,
                  gender: activeTab === "all" ? currentTab.filters.gender : filters.gender,
                  language: activeTab === "all" ? currentTab.filters.language : filters.language,
                  viewMode,
                  tab: activeTab as "recommended" | "favorites" | "all",
                }}
                onChange={handleFilterChange}
                onReset={handleReset}
                categories={["professional", "famous", "high_quality"]}
                options={filterOptions}
                counts={{ favorites: favoriteVoices.length }}
                isLoadingAll={isLoadingAllVoices}
                resultsLabel={activeTab === "all"
                  ? `Showing ${filteredVoices.length}${currentTab.searchInput ? '' : ` of ${allVoicesHasMore ? allVoicesTotalCount + '+' : allVoicesTotalCount}`} voices${currentTab.searchInput ? ` matching "${currentTab.searchInput}"` : ''}`
                  : `Showing ${visibleFilteredVoices.length} of ${filteredVoices.length} ${
                    activeTab === "favorites" ? "favorite voices" : "preselected voices"
                  }${filters.searchQuery ? ` matching "${filters.searchQuery}"` : ''}`
                }
              />
            </div>

            {filteredVoices.length === 0 ? (
              <VoiceEmptyState
                activeTab={activeTab}
                isLoadingAllVoices={isLoadingAllVoices}
                isLoadingRecommended={recommendedLoading}
                searchQuery={filters.searchQuery}
              />
            ) : (
              <VoiceGrid
                voices={activeTab === "all" ? filteredVoices : visibleFilteredVoices}
                viewMode={viewMode}
                playingVoiceId={playingVoiceId}
                favoriteVoiceIds={favoriteVoiceIds}
                visibleCount={activeTab === "all" ? filteredVoices.length : visibleVoiceCount}
                totalCount={activeTab === "all" ? allVoicesTotalCount : filteredVoices.length}
                hasMore={activeTab === "all" ? allVoicesHasMore : undefined}
                isTransitioning={isTabTransitioning}
                onPlayVoice={playVoicePreview}
                onToggleFavorite={handleToggleFavorite}
                onTryTextToSpeech={handleTryTextToSpeech}
                onLoadMore={activeTab === "all" ? loadMore : loadMoreVoices}
              />
            )}
          </div>
        </div>
      </div>

      <VoiceTtsSheet
        open={ttsSheetOpen}
        onOpenChange={(o) => {
          setTtsSheetOpen(o);
          if (!o) setTtsVoice(null);
        }}
        voiceId={ttsVoice?.id || null}
        voiceName={ttsVoice?.name}
      />

      <VoiceCreateModal
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onVoiceCreated={() => {
          refreshVoices();
          switchTab("recommended");
        }}
      />
    </>
  );
};

export default VoicePlaygroundPage;
